export const WELLNESS_SCORE_KEY = "wellness_score";

// The score is the stack total and hrv is not one of its inputs, so neither is a segment.
const KEYS_EXCLUDED_FROM_STACK = new Set([WELLNESS_SCORE_KEY, "hrv_score"]);

/**
 * The wellness score is derived from the other answers rather than collected, so charts
 * that plot or correlate a single survey metric leave it out of their metric list.
 */
export const isWellnessScoreMetric = (metric) =>
	metric?.metric_type === "wellness" && metric?.key === WELLNESS_SCORE_KEY;

// A UDM/UDF operand has to resolve to a number, so only these answer types can back one.
const FORMULA_WELLNESS_ANSWER_TYPES = new Set(["int", "float", "options"]);

/**
 * Whether a wellness catalog entry can be used as a UDM/UDF operand: an int, float or
 * options question, the derived wellness score, or a wellness user-defined metric (the
 * backend tags those `float`). Bodymap, string and any other answer type are skipped.
 */
export const isFormulaCompatibleWellnessMetric = (metric) =>
	metric?.key === WELLNESS_SCORE_KEY ||
	FORMULA_WELLNESS_ANSWER_TYPES.has(String(metric?.answer_type ?? "").toLowerCase());

/**
 * Shapes a wellness payload into the two inputs the wellness stacked bar chart takes:
 * one row per date holding every question's `<key>_score_contribution`, and the question
 * list those stack segments are named and coloured from.
 */
export const buildWellnessScoreStack = (wellness, wellnessMetrics = []) => {
	const labels = (wellnessMetrics ?? []).filter(
		(metric) =>
			!KEYS_EXCLUDED_FROM_STACK.has(metric?.key) && metric?.no_color === false
	);

	const rowsByDate = new Map();
	Object.values(wellness ?? {}).forEach((series) => {
		(series ?? []).forEach((row) => {
			if (!row?.date) return;
			rowsByDate.set(row.date, { ...rowsByDate.get(row.date), ...row });
		});
	});

	const rows = [...rowsByDate.values()].sort((a, b) =>
		a.date.localeCompare(b.date)
	);

	return { rows, labels };
};
